import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";

import * as request from "../util/request";
import { env } from "../util/env";
import { ReduxState, ReduxDispatch } from "./reducers";
import {
    AccountDto,
    AccountSummaryDto,
    AccountNameEditDto,
    WithdrawalRequestDto,
} from "../util/DTOs/AccountDtos";
import { showSnackbar } from "./snackbar";
import { formValueSelector } from "redux-form";
import logger from "util/logger";

export interface Accounts {
    [id: number]: AccountDto;
}

/**
 * Action factory + actions
 */
const actionFactory = actionCreatorFactory("HypoMobiili/Account");
const getAccountAction = actionFactory<number, AccountDto, string>(
    "GET_ACCOUNT"
);

const getAllAccountsAction = actionFactory<undefined, Accounts, string>(
    "GET_ALL_ACCOUNTS"
);
const saveWithdrawalRequestAction = actionFactory<
    number,
    WithdrawalRequestDto,
    string
>("SAVE_WITHDRAWALREQUEST");

const editAccountNameAction = actionFactory<{}, AccountNameEditDto, string>(
    "EDIT_ACCOUNT_NAME"
);

export function getAccount(id: number) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getAccountAction(id));

            const result = await request.get<AccountDto>(
                `${env.APP_API_HOST}/api/accounts/${id}`
            );

            dispatch(getAccountAction.done(result));
            return result;
        } catch (error) {
            dispatch(getAccountAction.failed("Error loading account data"));
            throw error;
        }
    };
}

export function getAllAccounts(excludeFixedTerm: boolean = false) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getAllAccountsAction(undefined));

            const result = await request.get<Array<AccountDto>>(
                `${env.APP_API_HOST}/api/accounts${
                    excludeFixedTerm ? "?excludeFixedTerm=true" : ""
                }`
            );

            let accounts: Accounts = {};
            result.forEach((account) => {
                accounts[account.id] = account;
            });

            dispatch(getAllAccountsAction.done(accounts));
        } catch (error) {
            dispatch(
                getAllAccountsAction.failed("Error loading all accounts data")
            );
            throw error;
        }
    };
}

export function editAccountName(data: AccountNameEditDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(editAccountNameAction({}));

            const result = await request.post<AccountNameEditDto>(
                `${env.APP_API_HOST}/api/accounts/${data.id}`,
                data
            );

            dispatch(editAccountNameAction.done(result));
        } catch (error) {
            logger.error(error);
            dispatch(editAccountNameAction.failed(""));
            dispatch(showSnackbar("Nimen vaihtaminen epäonnistui"));
        }
    };
}

export function saveWithdrawalRequestForm(data: WithdrawalRequestDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(saveWithdrawalRequestAction(data.id));

            const result = await request.post<WithdrawalRequestDto>(
                `${env.APP_API_HOST}/api/withdrawals`,
                data
            );

            dispatch(saveWithdrawalRequestAction.done(result));
        } catch (error) {
            dispatch(
                saveWithdrawalRequestAction.failed(
                    "Failed to save withdrawal request"
                )
            );
            dispatch(showSnackbar("Virhe nostopyynnön lähettämisessä"));
            throw error;
        }
    };
}

/**
 * The internal state of this reducer
 */
export class AccountState {
    accounts: Accounts;
    withdrawalRequest: WithdrawalRequestDto;
}

/**
 * The reducer.
 *
 * The module exports this as default
 */

const initialAccountState: AccountState = {
    accounts: {},
    withdrawalRequest: {
        id: 0,
        accountNumber: "",
        amount: 0,
        contactByEmail: false,
        contactByPhone: false,
    },
};

export const AccountReducer = reducerWithInitialState(initialAccountState)
    .case(getAccountAction.done, (state, action) => {
        return {
            ...state,
            accounts: {
                ...state.accounts,
                [action.payload.id]: action.payload,
            },
        };
    })
    .case(getAllAccountsAction.done, (state, action) => {
        return {
            ...state,
            accounts: action.payload,
        };
    })
    .case(editAccountNameAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(editAccountNameAction.done, (state, action) => {
        const id = action.payload.id;

        return {
            ...state,
            accounts: {
                ...state.accounts,
                [id]: {
                    ...state.accounts[id],
                    accountName: action.payload.accountName,
                },
            },
            loading: false,
        };
    })
    .case(editAccountNameAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(saveWithdrawalRequestAction.done, (state, action) => {
        return {
            ...state,
            withdrawalRequest: action.payload,
        };
    });

export default AccountReducer;

/**
 * Selectors
 */
export function getAccountData(state: ReduxState, id: number) {
    return (
        (state &&
            state.account &&
            state.account.accounts &&
            state.account.accounts[id]) ||
        false
    );
}

export function getAllAccountsData(state: ReduxState) {
    return state.account.accounts;
}

export function getAllAccountsToDeposit(state: ReduxState): Accounts {
    return Object.keys(state.account.accounts).reduce((accum, key) => {
        const account: AccountDto = state.account.accounts[key];

        if (account.canDeposit) {
            return {
                ...accum,
                [key]: account,
            };
        } else {
            return accum;
        }
    }, {});
}

export function getAllAccountsArray(state: ReduxState) {
    let accountArray: AccountDto[] = [];

    Object.keys(state.account.accounts).map((key) => {
        accountArray.push(state.account.accounts[key]);
    });

    return accountArray;
}

export function getSelectedAccount(state: ReduxState, form: string) {
    const selector = formValueSelector(form);
    const selectedAccount = selector(state, "accountNumber");
    return selectedAccount;
}

export function getLastWithdrawalRequest(state: ReduxState) {
    return state.account.withdrawalRequest;
}



// WEBPACK FOOTER //
// ./src/ducks/account.ts